import { useCallback, useMemo, useState } from "react";

import { TransactionsBlueIcon, TrashDark } from "../../../../assets";
import { updateCollaborator } from "../../../../service/organisation/OrganisationService";
import { SearchCollaboratorsResponse } from "../../../../service/query";
import { ResultData, Status } from "../../../../service/Shared";
import { getErrorMessageFromCode } from "../../../../service/ValidationErrorFormatter";
import {
  DataGridColumnDefinitionWithCustomCellFormatter,
  DataGridCursorDataLoadEventHandler,
  DataGridIconCellFormatterData,
  Toast,
} from "../../../../widget";

type AssociatedProjects = SearchCollaboratorsResponse["results"][number]["associatedProjects"];
type AssociatedProject = SearchCollaboratorsResponse["results"][number]["associatedProjects"][number];

const getLandholdingName = (project: AssociatedProject): string | null => {
  if (!project.groups?.length) return null;
  const landholding = project.groups.find((group) => group.type === "Landholding");
  if (landholding?.groupName) return landholding.groupName;
  return (
    project.groups
      .map((group) => group.groupName)
      .filter(Boolean)
      .join(", ") || null
  );
};

const formatValue = (value: string | null | undefined): string | null => value ?? null;

const formatArea = (area: string | null): string | null => (area ? `${area} ha` : null);

interface UseCompanyAssociationsProps {
  company: SearchCollaboratorsResponse["results"][number] | null;
  associatedProjects: AssociatedProjects;
  rowVersion: number;
  setRowVersion: (rowVersion: number) => void;
  onCompanyRefetch?: () => void;
  onViewTransactions: (project: AssociatedProject) => void;
}

interface UseCompanyAssociationsReturn {
  handleRemoveProject: (projectUuid: string) => Promise<void>;
  associatedProjectRows: ResultData[];
  dataGridColumns: DataGridColumnDefinitionWithCustomCellFormatter<
    DataGridIconCellFormatterData | string | null | undefined
  >[];
  onAssociatedProjectRowsGridChange: DataGridCursorDataLoadEventHandler;
}

export const useCompanyAssociations = ({
  company,
  associatedProjects,
  rowVersion,
  setRowVersion,
  onCompanyRefetch,
  onViewTransactions,
}: UseCompanyAssociationsProps): UseCompanyAssociationsReturn => {
  const [removingProjectUuid, setRemovingProjectUuid] = useState<string | null>(null);
  const handleRemoveProject = useCallback(
    async (objectUuid: string): Promise<void> => {
      if (!company || removingProjectUuid) return;
      setRemovingProjectUuid(objectUuid);
      const now = new Date().toISOString().replace(/\.\d{3}Z$/, "Z");

      const response = await updateCollaborator({
        addressCity: company.addressCity,
        addressCountryCode: company.addressCountryCode,
        addressLine1: company.addressLine1,
        addressLine2: company.addressLine2,
        addressPostcode: company.addressPostcode,
        addressRegion: company.addressRegion,
        associations: associatedProjects
          .filter((proj) => proj.uuid === objectUuid)
          .map((project) => ({
            endDate: now,
            objectType: "Project",
            objectUuid: project.uuid,
            role: project.role,
          })),
        collaboratorType: company.collaboratorType,
        description: company.description,
        email: company.email,
        externalReference: company.externalReference,
        name: company.name,
        organisationUuid: company.organisationUuid ?? "",
        phone: company.phone,
        rowVersion,
        uuid: company.uuid,
        website: company.website,
      });

      if (response.status === Status.Error) {
        Toast.error({
          message: getErrorMessageFromCode(response.errors?.[0]?.message ?? "ERROR_APPLICATION"),
        });
        setRemovingProjectUuid(null);
        return;
      }

      if (response.status === Status.Success && response.data?.rowVersion) {
        setRowVersion(response.data.rowVersion);
      }
      onCompanyRefetch?.();
      setRemovingProjectUuid(null);
    },
    [associatedProjects, company, onCompanyRefetch, removingProjectUuid, rowVersion, setRowVersion]
  );

  const dataGridColumns: DataGridColumnDefinitionWithCustomCellFormatter<
    DataGridIconCellFormatterData | string | null | undefined
  >[] = useMemo(
    () => [
      { key: "displayName", name: "Project", dataType: "string" },
      { key: "role", name: "Role", dataType: "string" },
      { key: "projectTypeDisplayName", name: "Project type", dataType: "string" },
      { key: "area", name: "Area", dataType: "string" },
      { key: "phase", name: "Phase", dataType: "string" },
      { key: "availableUnits", name: "Available units", dataType: "string" },
      {
        key: "transactions",
        name: "Transactions",
        dataType: "string",
        formatter: "icon",
        alignment: "center",
        sortable: false,
        minWidth: 104,
      },
      {
        key: "remove",
        name: "Remove",
        dataType: "string",
        formatter: "icon",
        alignment: "center",
        sortable: false,
        minWidth: 72,
      },
    ],
    []
  );

  const associatedProjectRows: ResultData[] = useMemo(
    () =>
      (associatedProjects as AssociatedProject[]).map((project) => ({
        uuid: project.uuid,
        displayName: formatValue(project.displayName),
        landholding: getLandholdingName(project),
        role: formatValue(project.role),
        projectTypeDisplayName: formatValue(project.projectTypeDisplayName),
        area: formatArea(project.areaHa),
        phase: formatValue(project.phase),
        availableUnits: project.unitQuantityAvailable,
        transactions: {
          action: () => onViewTransactions(project),
          icon: <TransactionsBlueIcon width={20} height={20} />,
        } as DataGridIconCellFormatterData,
        remove: {
          action: () => {
            if (removingProjectUuid === project.uuid) return;
            handleRemoveProject(project.uuid);
          },
          icon: <TrashDark width={20} height={20} />,
        } as DataGridIconCellFormatterData,
      })),
    [associatedProjects, handleRemoveProject, onViewTransactions, removingProjectUuid]
  );

  const onAssociatedProjectRowsGridChange: DataGridCursorDataLoadEventHandler = useCallback(async () => {
    return {
      resultData: associatedProjectRows,
      paging: {
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
        pageSize: associatedProjectRows.length,
        totalCount: associatedProjectRows.length,
      },
    };
  }, [associatedProjectRows]);

  return {
    handleRemoveProject,
    associatedProjectRows,
    dataGridColumns,
    onAssociatedProjectRowsGridChange,
  };
};
